import dayjs from 'dayjs';
import { ApplicantNameCell } from './ApplicantNameCell';
import { ViewMorePopover } from '@/components/ats/ViewMorePopover/ViewMorePopover';
import { jobsEmploymentsOptions } from '@/constants/job-constants';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import { KImage, KTag, KText } from '@/ui-library/components';
import { Flex, Link, Tooltip, Icon, Box } from '@chakra-ui/react';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';

const workTypeValueMap = Object.fromEntries(
  jobsEmploymentsOptions.map((opt) => [opt.value, opt.label])
);

const workplaceValueMap = {
  office: 'On-Site',
  remote: 'Remote',
  hybrid: 'Hybrid',
};

export const NameCell = (row) => {
  const atsApplication = row?.original?.ats_application;
  const { ats_job = {} } = row?.original?.job || {};
  const { id: jobId } = ats_job || {};
  return <ApplicantNameCell atsApplication={atsApplication} jobId={jobId} />;
};

/** Time in stage - seconds to human-readable */
export const TimeInStage = (row) => {
  const { time_in_stage, is_overdue, current_stage_sla_hours } =
    row?.original ?? {};
  const duration = time_in_stage ?? 0;
  const slaHours = Number(current_stage_sla_hours ?? 0);
  const slaDays = Number(duration) - slaHours / 24;
  return (
    <Flex w="100%" alignItems="center" gap="6px">
      <KText
        maxW="100%"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
        color={is_overdue ? 'pastel-1-dark' : 'secondary-700'}
      >
        {`${duration} ${duration === 1 ? 'day' : 'days'}`}
      </KText>
      {is_overdue && (
        <Tooltip
          label={`This candidate has been in this stage ${slaDays} days past the target time in stage`}
        >
          <Box cursor="pointer">
            <Icon as={InfoCircle} />
          </Box>
        </Tooltip>
      )}
    </Flex>
  );
};

/** Source (on application) - nested or flat */
export const SourceName = (row) => {
  const original = row?.original || {};
  const source = original.source || {};
  const atsCandidateSource = source.ats_candidate_source || {};
  const name =
    atsCandidateSource.name || original.source_name || source.name || '-';

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {name || '-'}
      </KText>
    </Flex>
  );
};

/** Credited to */
export const CreditedTo = (row) => {
  const original = row?.original || {};
  const creditedTo = original.credited_to || {};
  const name =
    creditedTo?.name ??
    original.credited_to_name ??
    (typeof creditedTo === 'string' ? creditedTo : '');
  const avatar = creditedTo?.avatar;

  return (
    <>
      {name ? (
        <Flex w="100%" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText
            maxW="100%"
            enableTooltip={true}
            isTruncated
            size="sm-regular"
            textTransform="capitalize"
          >
            {name}
          </KText>
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

/** Job name */
export const JobName = (row) => {
  const original = row?.original || {};
  const job = original.job || {};
  const atsJob = job.ats_job || original.ats_job || {};
  const jobId = atsJob.id || original.job_id;
  const name = atsJob.name || original.job_name || job.name || '-';

  return (
    <Link href={`/jobs/${jobId}`} target="_blank" w="100%">
      <KText
        maxW="100%"
        isTruncated
        enableTooltip={true}
        _hover={{
          color: 'primary-600',
          textDecor: 'underline',
          cursor: 'pointer',
        }}
      >
        {name || '-'}
      </KText>
    </Link>
  );
};

/** Stage name */
const parseStageNames = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return value.trim() ? [value.trim()] : [];
    }
  }
  return [];
};

const renderStageInitial = (items: string[]) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.map((name, i) => (
      <KText
        key={i}
        maxW="130px"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
      >
        {name}
      </KText>
    ))}
  </Flex>
);

const renderStageOthers = (list: string[]) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((name, i) => (
      <Flex key={i} alignItems="center" gap=".25em">
        <KText
          fontSize="sm"
          color="secondary-700"
          isTruncated
          enableTooltip={true}
        >
          {name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

export const StageName = (row) => {
  const original = row?.original || {};
  const stageName = original.stage_name ?? original.office_names;
  const stages = parseStageNames(stageName);

  return (
    <Flex w="100%">
      <ViewMorePopover
        list={stages}
        max={1}
        type="officeList"
        renderInitial={renderStageInitial}
        renderOthers={renderStageOthers}
        emptyMsg="-"
        trigger="hover"
      />
    </Flex>
  );
};

const normalizeToArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

const renderPeopleInitial = (items) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.map((person, i) => (
      <Flex key={i} alignItems="center" gap="6px">
        <KImage
          name={person?.name}
          src={person?.avatar}
          backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
        />
        <KText
          textTransform="capitalize"
          isTruncated
          enableTooltip={true}
          maxW="120px"
        >
          {person?.name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

const renderPeopleOthers = (list) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((person, i) => (
      <Flex key={i} alignItems="center" gap=".25em">
        <KImage
          name={person?.name}
          src={person?.avatar}
          backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
        />
        <KText
          textTransform="capitalize"
          isTruncated
          enableTooltip={true}
          maxW="100%"
        >
          {person?.name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

export const Recruiters = (row) => {
  const original = row?.original || {};
  const recruiters = original.recruiters ?? original.all_recruiter_names ?? [];
  const list = normalizeToArray(recruiters);

  return (
    <>
      {list?.length > 0 ? (
        <Flex w="100%">
          <ViewMorePopover
            list={list}
            max={1}
            type="people"
            renderInitial={renderPeopleInitial}
            renderOthers={renderPeopleOthers}
            emptyMsg="-"
            trigger="hover"
          />
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

export const HiringManagers = (row) => {
  const original = row?.original || {};
  const hiringManagers =
    original.hiring_managers ?? original.all_hiringmanager_names ?? [];
  const list = normalizeToArray(hiringManagers);

  return (
    <>
      {list?.length > 0 ? (
        <Flex w="100%">
          <ViewMorePopover
            list={list}
            max={1}
            type="people"
            renderInitial={renderPeopleInitial}
            renderOthers={renderPeopleOthers}
            emptyMsg="-"
            trigger="hover"
          />
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

export const DepartmentName = (row) => {
  const original = row?.original || {};
  const department = original.department ?? original.ats_department ?? {};
  const departmentName =
    department?.name ??
    original.department_name ??
    original.job_department_name ??
    '';

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {departmentName || '-'}
      </KText>
    </Flex>
  );
};

const parseOfficeNames = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object' && 'ats_office' in item) {
          const office = (item as { ats_office?: { name?: string } })
            .ats_office;
          return office?.name ?? '';
        }
        return String(item);
      })
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed
            .map((item: unknown) =>
              item && typeof item === 'object' && 'ats_office' in item
                ? ((item as { ats_office?: { name?: string } }).ats_office
                    ?.name ?? '')
                : String(item)
            )
            .filter(Boolean)
        : [];
    } catch {
      return value.trim() ? [value.trim()] : [];
    }
  }
  return [];
};

const renderOfficeInitial = (items: string[]) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.map((name, i) => (
      <KText
        key={i}
        maxW="130px"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
      >
        {name}
      </KText>
    ))}
  </Flex>
);

const renderOfficeOthers = (list: string[]) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((name, i) => (
      <Flex key={i} alignItems="center" gap=".25em">
        <KText
          fontSize="sm"
          color="secondary-700"
          isTruncated
          enableTooltip={true}
        >
          {name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

export const OfficeName = (row) => {
  const original = row?.original || {};
  const officeNames =
    original.office_names ?? original.offices ?? original.office_name ?? [];
  const offices = parseOfficeNames(officeNames);

  return (
    <Flex w="100%">
      <ViewMorePopover
        list={offices}
        max={1}
        type="officeList"
        renderInitial={renderOfficeInitial}
        renderOthers={renderOfficeOthers}
        emptyMsg="-"
        trigger="hover"
      />
    </Flex>
  );
};

export const Workplace = (row) => {
  const original = row?.original || {};
  const workplace = original.workplace ?? original.job_type ?? '';
  const label = workplaceValueMap[workplace] ?? workplace;

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {label || '-'}
      </KText>
    </Flex>
  );
};

export const WorkType = (row) => {
  const original = row?.original || {};
  const workType = original.work_type ?? original.job_employment_type ?? '';
  const label = workTypeValueMap[workType] ?? workType;

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {label || '-'}
      </KText>
    </Flex>
  );
};

export const ApplicationCreator = (row) => {
  const original = row?.original || {};
  const createdBy = original.created_by ?? original.application_creator ?? {};
  const name =
    createdBy?.name ?? (typeof createdBy === 'string' ? createdBy : '');
  const avatar = createdBy?.avatar;

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
            {name}
          </KText>
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

/** Offer created date */
export const OfferCreatedDate = (row) => {
  const date =
    row?.original?.offer_first_generated_at ??
    row?.original?.offer_created_date;
  if (!date) return <KText size="sm-regular">-</KText>;
  const formatted = dayjs(date).isValid()
    ? dayjs(date).format('MMM DD, YYYY')
    : '-';
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {formatted}
    </KText>
  );
};

/** Offer acceptance status */
export const OfferStatus = (row) => {
  const offerStatus = row?.original?.offer_status ?? '';
  const labelMap = {
    Sent: {
      label: 'Sent',
      color: undefined,
      bg: undefined,
      borderColor: 'transparent',
    },
    Accepted: {
      label: 'Accepted',
      color: 'pastel-2-dark',
      bg: 'pastel-2-light',
      borderColor: 'transparent',
    },
    Pending: {
      label: 'Pending',
      color: '#D98647',
      bg: '#FFF0E0',
      borderColor: 'transparent',
    },
    Rejected: {
      label: 'Rejected',
      color: 'pastel-1-dark',
      bg: 'pastel-1-light',
      borderColor: 'transparent',
    },
    Cancelled: {
      label: 'Cancelled',
      color: 'pastel-1-dark',
      bg: 'pastel-1-light',
      borderColor: 'transparent',
    },
  };
  const { label, color, bg } = labelMap[offerStatus] || {};
  return <KTag bg={bg} color={color} value={label || offerStatus || '-'} />;
};

/** Time to offer */
export const TimeToOffer = (row) => {
  const duration = row?.original?.time_to_offer ?? 0;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(duration)}
    </KText>
  );
};

/** Start date (offer & hired stages) */
export const StartDate = (row) => {
  const date = row?.original?.employee_start_date || '';
  if (!date) return <KText size="sm-regular">-</KText>;
  const formatted = dayjs(date).isValid()
    ? dayjs(date).format('MMM DD, YYYY')
    : '-';
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {formatted}
    </KText>
  );
};

/** Hired date */
export const HiredDate = (row) => {
  const date = row?.original?.hired_at;
  if (!date) return <KText size="sm-regular">-</KText>;
  const formatted = dayjs(date).isValid()
    ? dayjs(date).format('MMM DD, YYYY')
    : '-';
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {formatted}
    </KText>
  );
};

/** Time to hire */
export const TimeToHire = (row) => {
  const duration = row?.original?.time_to_hire ?? 0;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(duration)}
    </KText>
  );
};
